import { useEffect, useState } from "react";
import { PELinearProgressWithLabel } from "../components/linear-progress";
import {
  getSingleUploadPresignedS3Url,
  saveEnableVideoAttractor,
  saveVenueAppBackground,
  saveVenueIcon,
  updateVenueSetupStatusData,
  uploadToS3PresignedUrl,
} from "../api/new-api";
import UploadImageContainer from "../components/UploadImageContainer";
import { capitalize } from "lodash";
import {
  BellAlertIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";
const SetupStatusContainer = ({
  venue,
  venueData,
  fetchSelectedVenueData,
}: {
  venue: {
    value: string;
    label: string;
  };
  venueData: {
    hasTierLevels: boolean;
    hasVenueAppConfig: boolean;
    hasAppLandingPage: boolean;
    hasMobileNotifications: boolean;
    logoImgUrl: string;
    appBackgroundImgUrl: string;
    sqsQueuesCount: {
      hydraDmcUpdatesQueueCount: number;
      peDataCloudQueueCount: number;
      odysseyDataCloudQueueCount: number;
    };
    venueName: string;
    venueNameAbbrev: string;
    landingUrlName: string;
    videoAttractorsEnabled: boolean;
  };
  fetchSelectedVenueData: () => void;
}) => {
  console.log("venue", venue);

  const [localVenueData, setLocalVenueData] = useState(venueData);

  const [isVideoAttractorsEnabled, setIsVideoAttractorsEnabled] = useState(
    venueData.videoAttractorsEnabled
  );

  useEffect(() => {
    setLocalVenueData(venueData);
  }, [venueData]);

  // Function to determine queue status message and styles based on the count
  const getQueueStatus = (count: number) => {
    if (count === null || count === undefined) {
      return {
        message: "Unable to find Queue data",
        color: "red",
        icon: <ExclamationCircleIcon className="w-8" />, // Red error icon
      };
    } else if (count < 2000) {
      return {
        message: "Operational",
        color: "green",
        icon: <CheckCircleIcon className="w-8" />, // Green tick icon
      };
    } else if (count >= 2000 && count <= 5000) {
      return {
        message: "Moderate Alert",
        color: "#edcb06",
        icon: <BellAlertIcon className="w-8" />, // Yellow alert icon
      };
    } else {
      return {
        message: "High Warning",
        color: "red",
        icon: <ExclamationCircleIcon className="w-8" />, // Red warning icon
      };
    }
  };

  // Function to add spacing to camelCase keys
  const formatQueueName = (key: string) => {
    return capitalize(key.replace(/([a-z])([A-Z])/g, "$1 $2"));
  };
  // Main component to render the status
  const QueueStatusDisplay = () => {
    return (
      <div>
        {Object.entries(venueData.sqsQueuesCount).map(([key, count]: any) => {
          const { message, color, icon } = getQueueStatus(count);
          return (
            <div
              key={key}
              className="my-4 flex items-start gap-2 w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow"
            >
              <p className="font-normal">{formatQueueName(key)}:</p>
              <span>
                <p
                  className="flex flex-row gap-2"
                  style={{
                    color,
                  }}
                >
                  {icon} {message} (Count: {count})
                </p>
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div className="mt-5">
        <p className="w-full flex gap-2 items-center font-medium text-xl text-gray-700 dark:text-gray-400">
          Configure Setup and Status for: {venue.label}
        </p>
        <p className="w-full flex gap-2 items-center font-medium text-xl text-gray-700 dark:text-gray-400">
          Venue ID: {venue.value}
        </p>
      </div>

      <div>
        <VenueIcon
          logoImgUrl={localVenueData.logoImgUrl}
          fetchSelectedVenueData={fetchSelectedVenueData}
          selectedVenueId={venue.value}
        />

        <VenueBackground
          appBackgroundImgUrl={localVenueData.appBackgroundImgUrl}
          fetchSelectedVenueData={fetchSelectedVenueData}
        />

        <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
          <p className="w-full flex gap-2 items-center font-normal text-gray-700 dark:text-gray-400">
            Venue Name:
            <span>
              <input
                value={localVenueData.venueName}
                onChange={(e) =>
                  setLocalVenueData((prev: any) => ({
                    ...prev,
                    venueName: e.target.value,
                  }))
                }
                className="w-max p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </span>
            {venueData.venueName !== localVenueData.venueName && (
              <SaveCancelButton
                handleSave={() => {
                  updateVenueSetupStatusData(venue?.value, {
                    Name: localVenueData.venueName,
                  }).then((res) => {
                    if (res.data.success) {
                      fetchSelectedVenueData();
                    }
                  });
                }}
                handleCancel={() => {
                  setLocalVenueData((prev) => ({
                    ...prev,
                    venueName: venueData.venueName,
                  }));
                }}
              />
            )}
          </p>
        </div>

        <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
          <p className="w-full flex gap-2 items-center font-normal text-gray-700 dark:text-gray-400">
            Landing URL Name:
            <span>
              <input
                value={localVenueData.landingUrlName}
                onChange={(e) =>
                  setLocalVenueData((prev: any) => ({
                    ...prev,
                    landingUrlName: e.target.value,
                  }))
                }
                className="w-max p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </span>
            {venueData.landingUrlName !== localVenueData.landingUrlName && (
              <SaveCancelButton
                handleSave={() => {
                  updateVenueSetupStatusData(venue.value, {
                    LandingUrlName: localVenueData.landingUrlName,
                  }).then((res) => {
                    if (res.data.success) {
                      fetchSelectedVenueData();
                    }
                  });
                }}
                handleCancel={() => {
                  setLocalVenueData((prev) => ({
                    ...prev,
                    landingUrlName: venueData.landingUrlName,
                  }));
                }}
              />
            )}
          </p>
        </div>

        <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
          <p className="w-full flex gap-2 items-center font-normal text-gray-700 dark:text-gray-400">
            Venue Name Abbreviation:
            <span>
              <input
                value={localVenueData.venueNameAbbrev}
                onChange={(e) =>
                  setLocalVenueData((prev: any) => ({
                    ...prev,
                    venueNameAbbrev: e.target.value,
                  }))
                }
                className="w-max p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </span>
            {venueData.venueNameAbbrev !== localVenueData.venueNameAbbrev && (
              <SaveCancelButton
                handleSave={() => {
                  updateVenueSetupStatusData(venue?.value, {
                    NameAbbrev: localVenueData.venueNameAbbrev,
                  }).then((res) => {
                    if (res.data.success) {
                      fetchSelectedVenueData();
                    }
                  });
                }}
                handleCancel={() => {
                  setLocalVenueData((prev) => ({
                    ...prev,
                    venueNameAbbrev: venueData.venueNameAbbrev,
                  }));
                }}
              />
            )}
          </p>
        </div>

        <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
          <p className="w-full flex gap-2 items-center font-normal text-gray-700 dark:text-gray-400">
            Enable Video Attractors:
            <span>
              <select
                className="w-max p-2 border border-gray-300 rounded-lg"
                name="videoAttractors"
                id="videoAttractors"
                value={isVideoAttractorsEnabled ? "true" : "false"}
                onChange={(e) => {
                  setIsVideoAttractorsEnabled(e.target.value === "true");
                }}
              >
                <option value="true">ON</option>
                <option value="false">OFF</option>
              </select>
            </span>
            {isVideoAttractorsEnabled !== venueData.videoAttractorsEnabled && (
              <SaveCancelButton
                handleSave={async () => {
                  try {
                    await saveEnableVideoAttractor({
                      venueId: venue.value,
                      videoAttractorsEnabled: isVideoAttractorsEnabled,
                    });
                    fetchSelectedVenueData();
                  } catch (err) {
                    alert("Error saving video attractors");
                    console.log(err);
                  }
                }}
                handleCancel={() => {
                  setIsVideoAttractorsEnabled(venueData.videoAttractorsEnabled);
                }}
              />
            )}
          </p>
        </div>

        <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
          <p className="w-full flex gap-2 items-center font-normal text-gray-700 dark:text-gray-400">
            Tier Levels are Configured:
            <span
              style={{
                color: localVenueData.hasTierLevels ? "green" : "red",
              }}
            >
              {localVenueData.hasTierLevels ? "ONLINE" : "OFFLINE"}
            </span>
          </p>
        </div>

        <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
          <p className="w-full flex gap-2 items-center font-normal text-gray-700 dark:text-gray-400">
            Mobile OS Notifications are Configured:{" "}
            <span
              style={{
                color: localVenueData.hasMobileNotifications ? "green" : "red",
              }}
            >
              {localVenueData.hasMobileNotifications ? "ONLINE" : "OFFLINE"}
            </span>
          </p>
        </div>

        <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
          <p className="w-full flex gap-2 items-center font-normal text-gray-700 dark:text-gray-400">
            Mobile App is Configured:{" "}
            <span
              style={{
                color: localVenueData.hasVenueAppConfig ? "green" : "red",
              }}
            >
              {localVenueData.hasVenueAppConfig ? "ONLINE" : "OFFLINE"}
            </span>
          </p>
        </div>

        <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
          <p className="w-full flex gap-2 items-center font-normal text-gray-700 dark:text-gray-400">
            Venue Landing Page is Configured:{" "}
            <span
              style={{
                color: localVenueData.hasAppLandingPage ? "green" : "red",
              }}
            >
              {localVenueData.hasAppLandingPage ? "ONLINE" : "OFFLINE"}
            </span>
          </p>
        </div>
        <QueueStatusDisplay />
      </div>
    </div>
  );
};

export default SetupStatusContainer;

const VenueIcon = ({
  logoImgUrl,
  selectedVenueId,
  fetchSelectedVenueData,
}: {
  logoImgUrl: string;
  selectedVenueId: string;
  fetchSelectedVenueData: () => void;
}) => {
  const [iconFile, setIconFile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [percentCompleted, setPercentCompleted] = useState(0);

  const handleSave = async () => {
    setLoading(true);
    try {
      const presignedUrlResponseBanner = await getSingleUploadPresignedS3Url({
        imageFileName: iconFile.name,
      });

      if (presignedUrlResponseBanner) {
        const uploadUrl = presignedUrlResponseBanner.data.imageUploadUrl;
        // Step 2: Upload the file to S3 using the presigned URL
        await uploadToS3PresignedUrl({
          uploadUrl: uploadUrl,
          data: iconFile,
          progressCallback: (percentCompleted) => {
            console.log(`Upload progress: ${percentCompleted}%`);
            setPercentCompleted(percentCompleted);
          },
        });

        await saveVenueIcon(
          presignedUrlResponseBanner.data.imageKey,
          selectedVenueId
        );
        setTimeout(() => {
          fetchSelectedVenueData();
        }, 3000);
      }
    } catch (err) {
      console.log(err);
      setError("Error saving icon");
    } finally {
      setLoading(false);
      setIconFile(null);
      setPercentCompleted(0);
      setError("");
    }
  };

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setError("");

      const blobUrl = URL.createObjectURL(file);
      const img = new Image();
      img.src = blobUrl;
      img.onload = () => {
        if (img.width / img.height !== 1 / 1) {
          setError("Thumbnail image ratio should be 1:1");
          return;
        }
        setIconFile(file);
        return;
      };
    }
  };

  return (
    <div className="w-full">
      <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
        <div className={"flex flex-row gap-2 justify-center"}>
          <div className="w-full flex flex-col items-center justify-center gap-4">
            <p className="mb-2 flex gap-2 items-center font-normal text-gray-700 dark:text-gray-400">
              Venue Icon:
            </p>
            <img
              src={logoImgUrl}
              alt="logo image url"
              className="w-40 h-40 object-cover rounded-lg"
            />
          </div>

          <div className="w-full flex flex-col">
            <UploadImageContainer
              id="iconFile"
              label="Upload New Icon"
              blobUrl={iconFile && URL.createObjectURL(iconFile)}
              errorMessage={error}
              handleFileChange={handleFileChange}
              description="Required Ratio: 1:1"
            />

            {!loading && iconFile && (
              <button
                onClick={handleSave}
                type="button"
                className="mt-4 focus:outline-none text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
              >
                Save New Icon
              </button>
            )}
            {loading && (
              <div className="mt-4">
                <PELinearProgressWithLabel value={percentCompleted} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const VenueBackground = ({
  appBackgroundImgUrl,
  fetchSelectedVenueData,
}: {
  appBackgroundImgUrl: string;
  fetchSelectedVenueData: () => void;
}) => {
  const [backgroundFile, setBackgroundFile] = useState<any>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [percentCompleted, setPercentCompleted] = useState(0);

  const handleSave = async () => {
    setLoading(true);
    try {
      const presignedUrlResponseBanner = await getSingleUploadPresignedS3Url({
        imageFileName: backgroundFile.name,
      });

      if (presignedUrlResponseBanner) {
        const uploadUrl = presignedUrlResponseBanner.data.imageUploadUrl;
        // Step 2: Upload the file to S3 using the presigned URL
        await uploadToS3PresignedUrl({
          uploadUrl: uploadUrl,
          data: backgroundFile,
          progressCallback: (percentCompleted) => {
            console.log(`Upload progress: ${percentCompleted}%`);
            setPercentCompleted(percentCompleted);
          },
        });

        await saveVenueAppBackground(presignedUrlResponseBanner.data.imageKey);
        setTimeout(() => {
          fetchSelectedVenueData();
        }, 3000);
      }
    } catch (err) {
      console.log(err);
      setError("Error saving background");
    } finally {
      setLoading(false);
      setBackgroundFile(null);
      setPercentCompleted(0);
      setError("");
    }
  };

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setError("");

      const blobUrl = URL.createObjectURL(file);
      const img = new Image();
      img.src = blobUrl;
      img.onload = () => {
        if (img.width <= img.height) {
          setError("Background image should be in landscape orientation");
          return;
        }
        setBackgroundFile(file);
      };
    }
  };

  return (
    <div className="w-full">
      <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
        <div className={"flex flex-row gap-2 justify-center"}>
          <div className="w-full flex flex-col items-center justify-center gap-4">
            <p className="flex gap-2 items-center font-normal text-gray-700 dark:text-gray-400">
              Venue Background:
            </p>
            <img
              src={appBackgroundImgUrl}
              alt="background image url"
              className="w-full h-40 object-cover rounded-lg"
            />
          </div>

          <div className="w-full flex flex-col">
            <UploadImageContainer
              id="backgroundFile"
              label="Upload New Background"
              blobUrl={backgroundFile && URL.createObjectURL(backgroundFile)}
              errorMessage={error}
              handleFileChange={handleFileChange}
              description="Required Ratio: 16:9"
            />

            {!loading && backgroundFile && (
              <button
                onClick={handleSave}
                type="button"
                className="mt-4 focus:outline-none text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
              >
                Save New Icon
              </button>
            )}
            {loading && (
              <div className="mt-4">
                <PELinearProgressWithLabel value={percentCompleted} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SaveCancelButton = ({
  handleSave,
  handleCancel,
}: {
  handleSave: () => void;
  handleCancel: () => void;
}) => {
  return (
    <div className="ml-6 flex flex-row justify-end gap-2">
      <button
        onClick={handleSave}
        type="button"
        className="focus:outline-none text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
      >
        Save
      </button>
      <button
        onClick={handleCancel}
        type="button"
        className="focus:outline-none text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800"
      >
        Cancel
      </button>
    </div>
  );
};
